import { isObject } from "../utils/utils"
import { DrawMarginInteraction } from "./drawMarginInteraction"
import { IMapMeasuringService } from "./measuring/types"
import { IMapTopologyService } from "./topo/types"
import { IMapContainer } from "./types"
import { observeOpenlayersObject } from "./utils"
import { ol_Map, ol_geom } from "@omnia/exports/openlayers";


export abstract class DrawMarginContext<Map extends IMapContainer> {

    protected olmap: ol_Map
    protected measuringService: IMapMeasuringService

    protected interaction: DrawMarginInteraction

    protected _watcher: Function

    protected _width: number = 10
    protected _fullBoundary: boolean = false

    protected _currentDimensions: { area: number, length: number }


    constructor(protected readonly map: Map, protected readonly topo: IMapTopologyService) {
        this.olmap = map.getMap()
        this.measuringService = map.getMeasuringService()
    }

    dispose() {

        this.cancelEdit()

        if (this.interaction) {
            this.interaction.dispose()
            this.interaction = null
        }

    }

    get selection() {
        return this.interaction.selection
    }

    get width() {
        return this._width
    }

    set width(width: number) {
        this.setWidth(width)
    }

    protected setWidth(width: number) {
        this._width = width
        this.updateBuffer()
        this.refreshDimensions()
    }

    get fullBoundary() {
        return this._fullBoundary
    }

    set fullBoundary(value: boolean) {
        if (this.isActive()) {
            this._fullBoundary = value
            this.interaction.setFullBoundaryMargin(value)
            this.refreshDimensions()
        }
    }


    protected setupChangeWatcher(watcher: () => void) {

        if (this._watcher) {
            this._watcher()
        }

        this._watcher = observeOpenlayersObject(this.interaction, ["change", "move"], watcher)

    }

    private releaseWatcher() {
        if (this._watcher) {
            this._watcher()
            this._watcher = null
        }
    }


    cancelEdit() {

        this.releaseWatcher()

        this._currentDimensions = undefined

        if (this.interaction) {
            this.interaction.reset()
            this.interaction.setActive(false)
        }

    }

    canCommit() {

        if (this.isActive()) {
            return !!(this.interaction.selection && this.interaction.targetFeature)
        }

        return true

    }

    isActive() {
        return this.interaction && this.interaction.getActive()
    }

    getCurrentDimensions() {

        if (!this.interaction) {
            return null
        }

        if (!isObject(this._currentDimensions)) {

            const selection = this.interaction.getCurrentSelection()

            if (selection) {

                const margin = this.bufferMargin(selection, true)

                this._currentDimensions = {
                    area: Math.round(this.measuringService.getArea(margin)),
                    length: Math.round(this.measuringService.getLength(selection) + (2 * this.width))
                }

            }

        }

        return { ...this._currentDimensions }

    }

    refreshDimensions() {
        this._currentDimensions = undefined
    }

    updateBuffer() {
        this.interaction.refresh()
    }

    bufferMargin(line: ol_geom.LineString, diff: boolean): ol_geom.Geometry {

        let distance = this._width || 10

        distance = this.measuringService.getProjectedDistance(line.getFirstCoordinate(), distance);

        let r = this.topo.buffer2(line, distance);

        if (diff) {

            const { targetFeature } = this.interaction

            if (targetFeature) {

                const geom = targetFeature.getGeometry()
                const intersection = this.topo.intersection(geom, r, true)

                r = intersection && intersection.isValid() ? intersection : r

            }
        }

        return r.getOpenLayersGeometry()

    }


}
